/**
 * Here we define global effects that are not tied to a specific slice.
 */

import { startAppListening } from 'src/store/middleware/listenerMiddleware';
import { isAnyOf } from '@reduxjs/toolkit';
import { actionEsOnOpen, selectWsEsStatus } from '@skynet/data-ws-es';
import { skyRouter } from 'src/routes/sky-router';
import { requiresLoginCommand } from 'src/utils/routing/validation';
import { loginDomain } from 'src/store/auth/slice';
import { actionOnOpen, selectWsMmStatus } from '@skynet/data-ws-mm';
import { routerHistoryChange } from 'src/store/router/actions';
import { ApplicationDomain } from 'src/routes/types';
import { loansActions } from 'src/store/loans/slice';
import { tradesActions } from 'src/store/trading/traders/slice';
import { retainersProjectsActions } from 'src/store/retainers/projects/slice';
import { vaultsActions } from 'src/store/DEX/Vaults/slice';
import { selectTokenTenant } from '@skynet/data-auth-api';

let prevGlobalDomain: ApplicationDomain | undefined;

/**
 * Reset sidebar filters when navigating between domains.
 * This listener resets search input when user switch domains.
 */
startAppListening({
  actionCreator: routerHistoryChange,
  effect: (_, listenerApi) => {
    const domain = skyRouter.getDomain();

    // Skip if domain hasn't changed
    if (prevGlobalDomain === domain) {
      prevGlobalDomain = domain;
      return;
    }

    const previousDomain = prevGlobalDomain;
    prevGlobalDomain = domain;

    // Skip reset on initial load
    if (previousDomain === undefined) return;

    // Reset filters for the domain user is leaving
    switch (previousDomain) {
      case ApplicationDomain.Loans:
        listenerApi.dispatch(loansActions.setSearchInput(''));
        break;

      case ApplicationDomain.Retainers:
        listenerApi.dispatch(retainersProjectsActions.setSearchInput(''));
        break;

      case ApplicationDomain.Trading:
        listenerApi.dispatch(tradesActions.setSearchInput(''));

        break;
      case ApplicationDomain.DEX:
        listenerApi.dispatch(vaultsActions.setSearchInput(''));
        break;

      case ApplicationDomain.Admin:
      case ApplicationDomain.Profile:
        // No sidebar filters to reset for these domains
        break;
    }
  },
});

export const startGlobalEffects = () => {
  const wsConnectionOpenEffectAbort = startAppListening({
    matcher: isAnyOf(actionEsOnOpen, actionOnOpen),
    effect: async (action, listenerApi) => {
      const state = listenerApi.getState();
      const selectWsStatus =
        action.type === actionEsOnOpen.type ? selectWsEsStatus : selectWsMmStatus;
      const wsStatus = selectWsStatus(state);

      const domain = skyRouter.getDomain();
      const pathname = skyRouter.getPathname();

      if (!requiresLoginCommand(pathname) || wsStatus !== 'connected' || !domain) {
        return;
      }

      const tokenTenant = selectTokenTenant(state);
      const slug = tokenTenant?.slug ?? '';
      await listenerApi.dispatch(loginDomain({ domain, forceLogin: true, slug }));
    },
  });

  const routerHistoryChangeEffectAbort = startAppListening({
    actionCreator: routerHistoryChange,
    effect: async (action, listenerApi) => {
      const state = listenerApi.getState();
      const pathname = skyRouter.getPathname();
      const domain = skyRouter.getDomain();

      const selectWsStatus =
        domain === ApplicationDomain.Trading ? selectWsEsStatus : selectWsMmStatus;
      const wsStatus = selectWsStatus(state);

      if (!requiresLoginCommand(pathname) || wsStatus !== 'connected' || !domain) {
        return;
      }
      // loginDomain action checks if we logged in to domain and will skip login, if yes
      await listenerApi.dispatch(loginDomain({ domain }));
    },
  });

  return () => {
    wsConnectionOpenEffectAbort();
    routerHistoryChangeEffectAbort();
  };
};
